// Numeric precision constants + helpers.
//
// Goal: stop the 70+ scattered `.toFixed(8)` / `.toFixed(2)` calls from
// silently disagreeing about how many decimals a crypto amount or USD
// value should render with. New code should call `formatCrypto()` /
// `formatUsd()` instead of inline `.toFixed(…)`.
//
// Why a separate module from `src/lib/locale.ts`:
//   - `locale.ts` handles LOCALE-AWARE fiat formatting (NGN with the
//     Naira symbol, thousands separators per the user's region, etc.).
//     It speaks "in this country's currency convention."
//   - This module handles UNIT precision (how many digits past the
//     decimal point are meaningful for crypto vs. USD vs. wallet
//     balance displays). It speaks "in the unit's precision contract."
//
// They compose: a Naira amount uses `formatLocalAmount(n, "NGN", { …,
// maximumFractionDigits: DECIMALS_FIAT })`; a BTC amount uses
// `formatCrypto(n)` then renders next to a "BTC" suffix.

// ─── Precision counts ────────────────────────────────────────────────────────
// All amounts are expressed in "human" units (not satoshis / wei) so the
// values below are the human-facing decimal counts.

// Crypto amounts (BTC, ETH, SOL, USDT) — 8 decimals is the canonical
// Bitcoin satoshi precision, which we mirror across all assets so the
// UI is consistent even though ETH/USDT actually have 18 and 6 native
// decimals respectively. Trims trailing zeros in `formatCrypto`.
export const DECIMALS_CRYPTO = 8;

// USD / fiat — two decimals, the standard cents convention.
export const DECIMALS_FIAT = 2;

// Wallet balance displays (e.g. /admin/sweep, /balances bot command).
// Six decimals is enough to show a meaningful number for high-value
// chains (BTC/ETH) while still fitting on a single mobile line.
export const DECIMALS_BALANCE = 6;

// USD values where 4 decimals are useful (price feeds, drift % display).
// Distinct from DECIMALS_FIAT — fiat display rounds to cents; analytical
// USD values shouldn't.
export const DECIMALS_USD_ANALYTIC = 4;

// ─── Formatters ─────────────────────────────────────────────────────────────
// Each returns a string suitable for direct UI insertion. Trims trailing
// zeros from the fractional part so "0.10000000" renders as "0.1" —
// matches how a user would speak the number. Pass `pad: true` to keep
// trailing zeros (useful in tables where columns should align).

export function formatCrypto(value: number | string | null | undefined, opts: { pad?: boolean } = {}): string {
  const n = typeof value === "string" ? parseFloat(value) : value ?? NaN;
  if (!Number.isFinite(n)) return "—";
  const fixed = n.toFixed(DECIMALS_CRYPTO);
  return opts.pad ? fixed : trimTrailingZeros(fixed);
}

export function formatBalance(value: number | string | null | undefined, opts: { pad?: boolean } = {}): string {
  const n = typeof value === "string" ? parseFloat(value) : value ?? NaN;
  if (!Number.isFinite(n)) return "—";
  const fixed = n.toFixed(DECIMALS_BALANCE);
  return opts.pad ? fixed : trimTrailingZeros(fixed);
}

// USD formatted with 2 decimals. Use `formatLocalAmount` from
// `src/lib/locale.ts` for localized fiat display (currency symbol,
// thousands separators). This is the cents-only sibling for raw USD
// values that aren't localized.
export function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toFixed(DECIMALS_FIAT);
}

function trimTrailingZeros(s: string): string {
  // Keep at least one digit after the decimal point so "1.0" renders
  // as "1.0" not "1." — looks like a typo otherwise.
  if (!s.includes(".")) return s;
  return s.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}
